import { Box } from "@mui/material";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";

import CheckboxColumn from "components/Table/CheckboxColumn";
import StatusTag from "components/StatusTag";

import { formatTimestamp, formatDuration } from "utils/format";
import { DEVICE_STATUS_TAG, STATUS_TAG_LABEL } from "const";
import ConnectBar from "components/ConnectivityBar";

const proxyColumns = (columnHelper, permissions, getMspOrganizationRowDisabled) =>
  [
    permissions &&
      (permissions === "Admin" || permissions === "Super Admin") &&
      CheckboxColumn(columnHelper, "", {
        getRowDisabled: (row, table) => {
          if (getMspOrganizationRowDisabled?.(row, table)) return true;
          const selectedRows = table.getSelectedRowModel().rows;
          if (selectedRows.length === 0) return false;
          const isProxy = selectedRows[0].original.deviceType === "proxy";
          const lockedStatus = selectedRows[0].original.siteName;
          if (isProxy) return row.deviceType !== "proxy";
          if (!isProxy && lockedStatus === null)
            return row.deviceType === "proxy" || row.siteName !== null;
          if (!isProxy && lockedStatus !== null)
            return row.deviceType === "proxy" || row.siteName === null;
        },
      }),
    columnHelper.accessor("linkStatus", {
      header: "Status",
      enableColumnPinning: true,
      cell: (info) => (
        <StatusTag
          status={info.getValue()}
          statusMapping={DEVICE_STATUS_TAG}
          labelMapping={STATUS_TAG_LABEL}
        />
      ),
    }),
    columnHelper.accessor("deviceName", {
      header: "Device Name",
      enableColumnPinning: true,
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    columnHelper.accessor("siteName", {
      header: "Site",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("syncStatus", {
      header: "Sync Status",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("connect", {
      header: "Connectivity",
      cell: (info) => (
        <Box sx={{}}>
          {info.getValue() === "-" ? (
            "-"
          ) : (
            <ConnectBar
              info={info.getValue()}
              joinedOn={info.row.original.joinedOn}
            />
          )}
        </Box>
      ),
    }),
    columnHelper.accessor("lastOfflineTime", {
      header: "Last Seen",
      cell: (info) => {
        const status = info.row.original.linkStatus;
        const value = info.getValue();

        if (status === "online") return "Online";
        return value ? formatTimestamp(value) : "-";
      },
      meta: {
        exportValue: ({ row }) => {
          const status = row.original.linkStatus;
          const value = row.original.lastOfflineTime;

          if (status === "online") return "Online";
          return value ? formatTimestamp(value) : "-";
        },
      },
    }),
    columnHelper.accessor("macAddress", {
      header: "MAC Address",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("localIpAddr", {
      header: "Local Mgmt. IP",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("publicIpAddr", {
      header: "Public Mgmt. IP",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("modelName", {
      header: "Model Name",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    // Bug 33263 說明 proxy 固定顯示 -
    // 前端預防其他問題先檔
    columnHelper.accessor("hardwareVersion", {
      header: "Hardware Version",
      cell: () => <Box>-</Box>,
    }),
    // 2025/11/28 隱藏 Bug 32834 說明此功能暫不開放
    // columnHelper.accessor("serialNumber", {
    //   header: "Serial Number",
    //   cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    // }),
    // Bug 32834 說明此功能暫不開放
    // columnHelper.display({
    //   header: "License Status",
    //   id: "licenseStatus",
    //   cell: () => <Box>-</Box>,
    // }),
    // Bug 33154 說明在 proxy 此功能暫不開放
    // columnHelper.accessor("deviceTag", {
    //   header: "Tag",
    //   cell: (info) => {
    //     const value = info.getValue();

    //     if (!Array.isArray(value) || value.length === 0) {
    //       return "-";
    //     }

    //     return (
    //       <Box
    //         sx={{
    //           whiteSpace: "nowrap",
    //           overflow: "hidden",
    //           textOverflow: "ellipsis",
    //         }}
    //       >
    //         {value.map((item) => item.tagName).join(", ")}
    //       </Box>
    //     );
    //   },
    //   meta: {
    //     exportValue: ({ row }) => {
    //       const value = row.original.deviceTag;

    //       if (!Array.isArray(value) || value.length === 0) {
    //         return "-";
    //       }

    //       return value.map((item) => item.tagName).join(", ");
    //     }
    //   }
    // }),
    columnHelper.accessor("firmwareVersion", {
      header: "Firmware Version",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.display({
      header: "Clients",
      id: "clients",
      cell: () => <Box>-</Box>,
    }),
    columnHelper.accessor("traffic24HrUsage", {
      header: "Traffic Usage (24HR)",
      cell: (info) => {
        const value = info.getValue();
        if (value == null) {
          return <Box>-</Box>;
        }
        const mb = value / (1024 * 1024);
        return <Box>{mb.toFixed(2)} MB</Box>;
      },
      meta: {
        exportValue: ({ row }) => {
          const value = row.original.traffic24HrUsage;

          if (value == null) {
            return "-";
          }

          return `${(value / (1024 * 1024)).toFixed(2)} MB`;
        },
      },
    }),
    columnHelper.accessor("cpuStats", {
      header: "CPU Usage (%)",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("memoryStats", {
      header: "Memory Usage (%)",
      cell: (info) => {
        const value = info.getValue();

        return (
          <Box>{value != null ? `${Number(value).toFixed(2)}%` : "-"}</Box>
        );
      },
    }),
    columnHelper.accessor("uplinkDeviceName", {
      header: "Uplink Device",
      cell: (info) => {
        const value = info.getValue() || info.row.original.uplinkDeviceMac;
        return <Box>{value ? value : "-"}</Box>;
      },
      meta: {
        exportValue: ({ row }) => {
          const value =
            row.original.uplinkDeviceName || row.original.uplinkDeviceMac;
          return value ? value : "-";
        },
      },
    }),
    columnHelper.accessor("uptime", {
      header: "Uptime",
      cell: (info) => {
        const status = info.row.original.linkStatus;
        const lastSeen = info.row.original.lastOfflineTime;
        const uptime = info.getValue();
        const now = Math.floor(Date.now() / 1000);

        let display = "-";

        if (status === "offline") {
          if (lastSeen !== null) {
            display = formatTimestamp(lastSeen);
          }
        } else if (status === "online") {
          if (uptime !== null) {
            display = formatDuration(now - uptime);
          }
        }

        return (
          <Box sx={{ color: status === "offline" ? "#AAAAAA" : "inherit" }}>
            {display} {/* 算法參考#1094 */}
          </Box>
        );
      },
      meta: {
        exportValue: ({ row }) => {
          const status = row.original.linkStatus;
          const lastSeen = row.original.lastOfflineTime;
          const uptime = row.original.uptime;
          const now = Math.floor(Date.now() / 1000);

          let display = "-";

          if (status === "offline") {
            if (lastSeen !== null) {
              display = formatTimestamp(lastSeen);
            }
          } else if (status === "online") {
            if (uptime !== null) {
              display = formatDuration(now - uptime);
            }
          }

          return display;
        },
      },
    }),
    columnHelper.accessor("joinedOn", {
      header: "Join On",
      cell: (info) => (
        <Box>{info.getValue() ? formatTimestamp(info.getValue()) : "-"}</Box>
      ),
      meta: {
        exportValue: ({ row }) => {
          const value = row.original.joinedOn;
          return value ? formatTimestamp(value) : "-";
        },
      },
    }),
    columnHelper.accessor("description", {
      header: "Description",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("address", {
      header: "Address",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
  ].filter(Boolean);

export default proxyColumns;
